import { useState } from "react";
import useApi from "./useApi";
import usePage from "./usePage";

const useEntity = () => {
  const { getData, postData } = useApi();
  const { showMessage } = usePage();

  const fieldError = {
    error: true,
    message: "",
    name: "",
  };

  const [entityName, setEntityName] = useState(null);

  const [entityData, setEntityData] = useState(null);
  const [entityList, setEntityList] = useState([]);
  const [initialValues, setInitialValues] = useState(null);
  const [isSuccessful, setIsSuccessful] = useState(true);
  const [message, setMessage] = useState("");
  const [fieldErrors, setFieldErrors] = useState([]);
  const [entitySchema, setEntitySchema] = useState(null);
  const [emptyObject, setEmptyObject] = useState(null);
  const [saved, setSaved] = useState(false);
  const [recordCount, setRecordCount] = useState(0);
  const [deleted, setDeleted] = useState(false);
  const [ids, setIds] = useState([]);

  const setEntity = (name, init) => {
    initialize(init);
    setEntityName(name);
    getEntitySchema(name, init);
  };

  const initialize = (init) => {
    setEntityData(null);
    setEntityList([]);
    setIsSuccessful(true);
    setMessage("");
    setFieldErrors([]);
    setEntitySchema(null);
    setInitialValues(init);
    setSaved(false);
  };

  const getEntitySchema = async (name, init = {}) => {
    if (!name) return;

    const results = await postData({
      entityName: name,
      action: "getEmptyObject",
    });

    if (results.isSuccessful) {
      setEntitySchema(results.data);

      const fieldNames = Object.keys(results.data);
      let obj = {};
      for (const field of fieldNames) {
        obj[field] = results.data[field].value;
      }

      obj = { ...obj, ...init };

      setEmptyObject(obj);
    }
  };

  const clearEntityData = () => {
    setEntityData(emptyObject);
    setSaved(false);
  };

  const getRecordCount = async (filter) => {
    const results = await postData({
      entityName: entityName,
      action: "getRecordCount",
      filter: filter || null,
    });

    if (results.isSuccessful) {
      setRecordCount(results.data.recordCount);
    } else {
      setRecordCount(0);
    }
  };

  const getEntityData = async (entityId) => {
    if (!entityName || !entityId) return;

    setMessage("");
    setSaved(false);
    setEntityData(null);

    const results = await getData({
      entityName: entityName,
      id: entityId,
      data: {
        id: entityId,
      },
    });

    if (results.isSuccessful) {
      setEntityData(results.data);
    } else {
      setEntityData(entitySchema);
      showMessage(results.message, "error");
    }

    setIsSuccessful(results.isSuccessful);
    setMessage(results.message);
  };

  const getEntityList = async (filter) => {
    setMessage("");
    setSaved(false);
    setEntityList(null);

    const results = await getData({
      entityName,
      filter: filter || null,
    });

    if (results.isSuccessful) {
      setEntityList(results.data);
    } else {
      setEntityList([]);
    }

    setIsSuccessful(results.isSuccessful);
    setMessage(results.message);
  };

  const getPage = async (
    pageNumber,
    recordsPerPage,
    filter,
    orderBy,
    order,
  ) => {
    setMessage("");
    setSaved(false);
    setEntityList(null);
    setIds(null);

    const args = {
      entityName,
      action: "getPage",
      filter: filter || null,
      orderBy: orderBy || null,
      order: order || null,
      data: {
        pageNumber,
        recordsPerPage,
        orderBy: orderBy || null,
        order: order || null,
      },
    };

    const results = await postData(args);

    if (results.isSuccessful) {
      setEntityList(results.data);
      setRecordCount(results.entityCount);
      setIds(results.ids);
    } else {
      setEntityList([]);
    }

    setIsSuccessful(results.isSuccessful);
    setMessage(results.message);
  };

  const getFieldMessage = (name) => {
    const fieldMessage = {
      error: false,
      message: "",
    };

    if (fieldErrors && fieldErrors.length > 0) {
      const field = fieldErrors.find((f) => f.name === name);
      if (field) {
        fieldMessage.error = field.error;
        fieldMessage.message = field.message;
      }
    }

    return fieldMessage;
  };

  const validateEntityData = async () => {
    setIsSuccessful(true);
    setMessage("");

    const errors = [];

    const fieldNames = Object.keys(entitySchema);
    for (const fieldName of fieldNames) {
      if (!entitySchema[fieldName].validation) continue;

      switch (entitySchema[fieldName].validation) {
        case "required":
          if (!entityData[fieldName]) {
            errors.push({ ...fieldError, name: fieldName, message: "missing" });
          }
          break;
      }
    }

    setFieldErrors(errors);
    if (errors && errors.length > 0) {
      setIsSuccessful(false);
      showMessage("fieldErrors", "error");
    } else {
      await saveEntityData();
    }
  };

  const saveEntityData = async (data = null) => {
    setMessage("");
    setFieldErrors([]);
    setSaved(false);

    let action = "add";
    if (entityData && entityData.id) action = "update";
    else if (data && data.id) action = "update";

    const results = await postData({
      entityName: entityName,
      action: action,
      data: data ? data : entityData,
    });

    if (results.isSuccessful) {
      setSaved(true);
      showMessage("saved", "success");
    } else if (results.fieldErrors && results.fieldErrors.length > 0) {
      setFieldErrors(results.fieldErrors);
      showMessage("fieldErrors", "error");
    }

    setIsSuccessful(results.isSuccessful);
    setMessage(results.message);
  };

  const deleteEntity = async (id) => {
    setMessage("");
    setFieldErrors([]);
    setDeleted(false);

    const results = await postData({
      entityName: entityName,
      action: "delete",
      id: id ? id : entityData.id,
    });

    if (results.isSuccessful) {
      setDeleted(true);
      showMessage("deleted", "success");
    }
    if (results.fieldErrors && results.fieldErrors.length > 0) {
      setFieldErrors(results.fieldErrors);
      showMessage("fieldErrors", "error");
    }

    setIsSuccessful(results.isSuccessful);
    setMessage(results.message);
  };

  const updateField = async (fieldName, value, save = false) => {
    setMessage(null);

    if (!save) {
      setEntityData((current) => {
        return { ...current, [fieldName]: value };
      });
      return;
    }

    let data = { ...entityData, [fieldName]: value };
    setEntityData(data);

    await saveEntityData(data);
  };

  return {
    setEntity,
    entityName,
    setEntityName,
    emptyObject,
    entitySchema,
    entityData,
    setEntityData,
    entityList,
    ids,
    setInitialValues,
    clearEntityData,
    getEntityData,
    saveEntityData,
    getEntityList,
    updateField,
    isSuccessful,
    message,
    setMessage,
    validateEntityData,
    fieldErrors,
    getFieldMessage,
    setSaved,
    saved,
    getRecordCount,
    recordCount,
    getPage,
    deleteEntity,
    deleted,
  };
};

export default useEntity;
